import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ComponentSourceType, OperationCodeDto } from 'app/api/models';

@Component({
    selector: 'app-assembly-process-popup',
    templateUrl: './assembly-process-popup.component.html',
    standalone: false
})
export class AssemblyProcessPopupComponent implements OnInit {
  public itemForm: FormGroup;

  public operationCodes: OperationCodeDto[] = [];
  public filteredOpCodes: OperationCodeDto[] = [];
  public costCentres: { code: string; name: string; rate: number }[] = [];
  public bomItemOptions: { lineKey: number; bomItemId: number | null; clientLineId: number | null; level: string; partNumber: string }[] = [];

  public ComponentSourceType = ComponentSourceType;

  // True when this popup was opened against the overall product (assembly-level)
  // rather than a specific BOM line. The "Applies To" picker is hidden in that
  // case because the answer is always "the whole product" — leaving it visible
  // is dead UI that invites accidental retargeting to a component line.
  public isOverallLevel = false;

  // hh:mm display mirror of runPerUnitHours (which stores decimal hours).
  // Edits round to whole minutes; the underlying form control still receives
  // a decimal-hours number so save/persist semantics are unchanged.
  public runTimeText = '00:00';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<AssemblyProcessPopupComponent>,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.operationCodes = this.data.operationCodes || [];
    this.filteredOpCodes = [...this.operationCodes];
    this.costCentres = this.data.costCentres || [];
    this.bomItemOptions = this.data.bomItemOptions || [];
    this.buildItemForm(this.data.payload);
  }

  selectOpCode(event: MatAutocompleteSelectedEvent) {
    var op = this.operationCodes.find(x => x.code == event?.option.value);
    if(!op) return;

    this.itemForm.controls['operationName'].setValue(op.name);

    // Reads the row's flag rather than a hardcoded 'SUBCO' comparison: the code is
    // settings-editable now, so renaming it must not silently kill the subcontract flip.
    if(op.isSubcontract)
      this.itemForm.controls['sourceType'].setValue(ComponentSourceType.ThirdParty);
  }

  onSourceTypeChange(value: ComponentSourceType) {
    this.itemForm.controls['isSubcontracted'].setValue(value === ComponentSourceType.ThirdParty);
  }
    
  filterOpCodes(event: any) {
      var text = event.target.value;
      // Match on the code as well as the name: the field shows the code, so typing "SUB"
      // previously filtered everything out.
      var term = (text ?? '').toLowerCase();
      this.filteredOpCodes = this.operationCodes.filter(m =>
        (m.name ?? '').toLowerCase().includes(term) || (m.code ?? '').toLowerCase().includes(term));
      // this.cdr.detectChanges();
  }

  costCentreChanged(event: MatSelectChange) {
      var code = event.value;
      var cc = this.costCentres.find(x => x.code == code);
      if(cc)
        this.itemForm.controls['costCentreRate'].setValue(cc.rate);
  }

  buildItemForm(item) {
    const initialSourceType = item.sourceType ?? (item.isSubcontracted ? ComponentSourceType.ThirdParty : ComponentSourceType.Production);

    // Unified owning-line identity: real bomItemId OR negative clientLineId
    const initialLineKey = (item.bomItemId && item.bomItemId > 0) ? item.bomItemId
      : (item.clientLineId && item.clientLineId < 0) ? item.clientLineId
      : null;

    // Overall-level when both ids are absent — used to hide the "Applies To"
    // picker since it can only ever resolve to the overall product anyway.
    this.isOverallLevel = initialLineKey == null;

    this.itemForm = this.fb.group({
      bomAssemblyProcessId: [item.bomAssemblyProcessId || null],
      lineKey:              [initialLineKey],
      sourceType:           [initialSourceType,                     Validators.required],
      sequence:             [item.sequence || null,                 [Validators.required, Validators.min(10)]],
      operationCode:        [item.operationCode || '',              Validators.required],
      operationName:        [item.operationName || '',              Validators.required],
      costCentreCode:       [item.costCentreCode || '',             Validators.required],
      costCentreRate:       [item.costCentreRate || 0,              [Validators.required, Validators.min(0)]],
      isSubcontracted:      [initialSourceType === ComponentSourceType.ThirdParty],
      subcontractorName:        [item.subcontractorName || ''],
      subcontractorHourlyRate:  [item.subcontractorHourlyRate || 0, [Validators.min(0)]],
      setupHours:               [item.setupHours || 0,              [Validators.required, Validators.min(0)]],
      runPerUnitHours:          [item.runPerUnitHours || 0,         [Validators.required, Validators.min(0)]],
      notes:                    [item.notes || '']
    });

    // Seed the hh:mm display from the current decimal-hours value.
    this.runTimeText = this.formatHoursToHm(item.runPerUnitHours);
  }

  // ---- hh:mm <-> decimal-hours conversion for the Run Time field ----------
  // Decimal hours (e.g. 0.1586974564654) are unreadable; the input is presented
  // as hh:mm with minute precision. The underlying form control still holds a
  // number, so persistence is unchanged.

  formatHoursToHm(hours: number | null | undefined): string {
    const n = Number(hours);
    if (!isFinite(n) || n < 0) return '00:00';
    const totalMinutes = Math.round(n * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  // Returns null when the input is not in h(h):mm form so the caller can revert.
  parseHmToHours(text: string): number | null {
    if (text == null) return null;
    const match = String(text).trim().match(/^(\d+):([0-5]?\d)$/);
    if (!match) return null;
    const h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    if (!isFinite(h) || !isFinite(m)) return null;
    return h + (m / 60);
  }

  onRunTimeBlur() {
    const parsed = this.parseHmToHours(this.runTimeText);
    if (parsed == null) {
      // Bad input — revert display to whatever the form control still holds.
      this.runTimeText = this.formatHoursToHm(this.itemForm.controls['runPerUnitHours'].value);
      return;
    }
    this.itemForm.controls['runPerUnitHours'].setValue(parsed);
    this.runTimeText = this.formatHoursToHm(parsed);
  }

  submit() {
    // Split lineKey into bomItemId (real id) OR clientLineId (negative correlation key)
    const v = this.itemForm.value;
    const lineKey: number | null = v.lineKey ?? null;
    const result: any = {
      ...v,
      bomItemId: (lineKey != null && lineKey > 0) ? lineKey : null,
      clientLineId: (lineKey != null && lineKey < 0) ? lineKey : null,
    };
    delete result.lineKey;
    this.dialogRef.close(result);
  }
}
